/**
 * Lien d'invitation tuteur → élève/étudiant (migration 0241) : permet à une
 * personne SANS COMPTE Jeraisonne de s'inscrire (ou de se connecter si elle en
 * a déjà un) et d'être automatiquement liée au tuteur en un seul geste. Deux
 * modes : 'student' (matières, tutor_links) / 'academic' (mentorat académique
 * alternance, aucune matière, student_profiles.academic_tutor_id). Table/RPC
 * postérieures aux types générés (`types.generated.ts` n'est jamais régénéré,
 * cf. CLAUDE.md) → client générique.
 */

import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type { SupabaseClient } from '@supabase/supabase-js';
import type { SubjectSlug } from '@jeraisonne/db';
import { supabase } from '@/lib/supabase';

const db = supabase as unknown as SupabaseClient;

export type TutorInviteKind = 'student' | 'academic';

export interface TutorInviteLink {
  id: string;
  token: string;
  kind: TutorInviteKind;
  subjects: SubjectSlug[];
  useCount: number;
  lastUsedAt: string | null;
  createdAt: string;
}

export interface TutorInvitePublicContext {
  tutorDisplayName: string;
  kind: TutorInviteKind;
  subjects: SubjectSlug[];
}

export interface RedeemTutorInviteResult {
  kind: TutorInviteKind;
  tutorDisplayName: string;
  alreadyLinked: boolean;
}

const INVITE_ERROR_LABELS: Record<string, string> = {
  invalid_invite: "Ce lien d'invitation n'existe pas.",
  invite_revoked: "Ce lien d'invitation n'est plus valide.",
  academic_mentoring_disabled: 'Ce tuteur ne propose plus de mentorat académique.',
  not_alternant: 'Cette invitation est réservée aux étudiants en alternance.',
  role_mismatch: 'Ce lien est réservé aux élèves.',
  not_a_tutor: 'Seul un tuteur peut générer un lien d’invitation.',
  not_authenticated: 'Connecte-toi pour continuer.',
  invalid_kind: 'Type de lien invalide.',
};

/** Traduit le message brut d'une erreur RPC (code Postgres) en texte affichable. */
export function translateInviteError(rawMessage: string): string {
  return INVITE_ERROR_LABELS[rawMessage] ?? 'Une erreur est survenue. Réessaie plus tard.';
}

/** URL partageable d'un lien d'invitation. */
export function buildInviteUrl(origin: string, token: string): string {
  return `${origin}/rejoindre/${token}`;
}

/** Liens actifs du tuteur courant (au plus un par mode). */
export function useTutorInviteLinks(tutorId: string | undefined) {
  return useQuery({
    queryKey: ['tutor-invite-links', tutorId],
    enabled: !!tutorId,
    queryFn: async (): Promise<TutorInviteLink[]> => {
      const { data, error } = await db
        .from('tutor_invite_links')
        .select('id, token, kind, subjects, use_count, last_used_at, created_at')
        .eq('tutor_id', tutorId!)
        .eq('is_active', true)
        .order('created_at');
      if (error) throw new Error(error.message);
      type Row = {
        id: string;
        token: string;
        kind: TutorInviteKind;
        subjects: SubjectSlug[];
        use_count: number;
        last_used_at: string | null;
        created_at: string;
      };
      return ((data ?? []) as Row[]).map((row) => ({
        id: row.id,
        token: row.token,
        kind: row.kind,
        subjects: row.subjects,
        useCount: row.use_count,
        lastUsedAt: row.last_used_at,
        createdAt: row.created_at,
      }));
    },
  });
}

export interface CreateTutorInviteLinkInput {
  tutorId: string;
  kind: TutorInviteKind;
  subjects: SubjectSlug[];
}

/** Génère (ou réutilise/actualise) le lien actif du tuteur pour un mode donné. */
export function useCreateTutorInviteLink() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (input: CreateTutorInviteLinkInput): Promise<{ token: string }> => {
      const { data, error } = await db
        .rpc('create_tutor_invite_link', { p_kind: input.kind, p_subjects: input.subjects })
        .single();
      if (error) throw new Error(error.message);
      const row = data as { id: string; token: string };
      return { token: row.token };
    },
    onSuccess: (_data, variables) => {
      qc.invalidateQueries({ queryKey: ['tutor-invite-links', variables.tutorId] });
    },
  });
}

/** Révoque un lien d'invitation (is_active=false — jamais de suppression). */
export function useRevokeTutorInviteLink(tutorId: string | undefined) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (linkId: string) => {
      const { error } = await db
        .from('tutor_invite_links')
        .update({ is_active: false, revoked_at: new Date().toISOString() })
        .eq('id', linkId);
      if (error) throw new Error(error.message);
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ['tutor-invite-links', tutorId] }),
  });
}

/** Contexte public d'un lien par son token — appelable sans session (page d'atterrissage). */
export async function fetchTutorInvitePublic(token: string): Promise<TutorInvitePublicContext> {
  const { data, error } = await db.rpc('get_tutor_invite_link_public', { p_token: token }).single();
  if (error) throw new Error(error.message);
  const row = data as {
    tutor_display_name: string;
    kind: TutorInviteKind;
    subjects: SubjectSlug[];
  };
  return { tutorDisplayName: row.tutor_display_name, kind: row.kind, subjects: row.subjects };
}

/** Consomme le lien pour l'utilisateur courant (auth.uid()) — crée/active le lien tuteur⇄élève. */
export async function redeemTutorInviteLink(token: string): Promise<RedeemTutorInviteResult> {
  const { data, error } = await db.rpc('redeem_tutor_invite_link', { p_token: token }).single();
  if (error) throw new Error(error.message);
  const row = data as {
    kind: TutorInviteKind;
    tutor_display_name: string;
    already_linked: boolean;
  };
  return {
    kind: row.kind,
    tutorDisplayName: row.tutor_display_name,
    alreadyLinked: row.already_linked,
  };
}
